import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import GoogleReviews from "../components/GoogleReviews";
import { usePageMeta, ORG_JSON_LD } from "../lib/usePageMeta";
import { Award, ShieldCheck, Users, MapPin, Heart, Target } from "lucide-react";

const META = {
  seo: {
    title: "About CV Folks · UK-Based Professional CV Writers · London",
    description:
      "CV Folks is a London-based CV writing service delivering hand-written CVs across the UK. Meet our writers, methodology and the 60-day interview guarantee.",
    keywords: "about cv folks, uk cv writers, london cv writing service",
  },
  jsonLd: ORG_JSON_LD,
};

const VALUES = [
  { icon: Users, title: "UK writers, no outsourcing", text: "Every CV is hand-crafted by a UK-based writer who knows your industry." },
  { icon: Target, title: "Recruiter-informed", text: "Our methodology comes from years working with UK recruiters and hiring managers." },
  { icon: Heart, title: "Genuinely tailored", text: "No two CVs look the same. We write for your target role, seniority and story." },
  { icon: ShieldCheck, title: "Guarantees, not promises", text: "60-day interview guarantee. 6–12 months of free revisions. In writing." },
];

const STATS = [
  { n: "4,000+", label: "CVs delivered" },
  { n: "4.8★", label: "Google rating" },
  { n: "60-day", label: "Interview guarantee" },
  { n: "24–48h", label: "Typical turnaround" },
];

export default function AboutPage() {
  usePageMeta(META);
  return (
    <>
      <Navbar />
      <main data-testid="about-page" className="pt-24 md:pt-32 bg-background">
        <section className="max-w-5xl mx-auto px-6 md:px-10 pb-14 md:pb-20">
          <p className="label-eyebrow">About CV Folks</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mt-3 leading-[1.05]">
            A UK CV writing service built around one thing: your next interview.
          </h1>
          <p className="text-base md:text-lg text-foreground/70 mt-6 max-w-3xl">
            We&apos;re a small, London-based team of professional CV writers. We started
            CV Folks because we kept seeing brilliant candidates lose out to
            weaker ones — purely because of the way their story was written.
            Since then, we&apos;ve helped thousands of UK professionals land better roles.
          </p>
          <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm">
            <MapPin className="h-4 w-4 text-brand" />
            354 Gray&apos;s Inn Rd, London WC1X 8BP · UK-wide, remote-first
          </div>
        </section>

        {/* Stats */}
        <section className="bg-muted/30 py-14">
          <div
            data-testid="about-stats"
            className="max-w-5xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center"
          >
            {STATS.map((s) => (
              <div key={s.label} className="rounded-2xl bg-card border border-border p-6">
                <p className="font-serif text-3xl md:text-4xl font-bold text-brand">{s.n}</p>
                <p className="text-xs uppercase tracking-widest text-foreground/60 mt-1">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Values */}
        <section className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <p className="label-eyebrow text-center">What we stand for</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-center mt-3">
            Our values.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {VALUES.map((v) => (
              <div
                key={v.title}
                data-testid={`about-value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="rounded-2xl border border-border bg-card p-6 flex gap-4"
              >
                <div className="h-10 w-10 rounded-full bg-brand/10 text-brand flex items-center justify-center shrink-0">
                  <v.icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-bold">{v.title}</h3>
                  <p className="text-sm text-foreground/70 mt-1.5">{v.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Guarantee */}
        <section className="max-w-4xl mx-auto px-6 md:px-10 pb-24">
          <div className="rounded-3xl border border-brand/25 bg-brand/5 p-8 md:p-12 text-center">
            <Award className="h-10 w-10 text-brand mx-auto" />
            <h2 className="font-serif text-2xl md:text-3xl font-bold mt-4">
              60-Day Interview Guarantee.
            </h2>
            <p className="text-base text-foreground/80 mt-3 max-w-2xl mx-auto">
              If you don&apos;t land an interview within 60 days of receiving your final CV,
              we&apos;ll rewrite it &mdash; free of charge &mdash; and give you extra resources and support
              to help you land interviews faster. That&apos;s how confident we are in our work.
            </p>
            <Link
              to="/pricing"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand text-white px-6 py-3 text-sm font-semibold hover:opacity-90"
            >
              See our packages
            </Link>
          </div>
        </section>

        <GoogleReviews />
      </main>
      <Footer />
    </>
  );
}
